import { Modal, TextField, Select } from './forms';
/**
 * LOG ERROR MODAL
 *
 * Quick form to log a picking error.
 */

import { useState } from 'react';
import {
  BlockStack,
  InlineStack,
  Banner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface LogErrorModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  orderId?: number;
  orderNumber?: string;
}

export default function LogErrorModal({
  open,
  onClose,
  onSaved,
  orderId,
  orderNumber,
}: LogErrorModalProps) {
  const { fetchApi, loading } = useApi();

  const [errorType, setErrorType] = useState('wrong_item');
  const [productSku, setProductSku] = useState('');
  const [expectedSku, setExpectedSku] = useState('');
  const [actualSku, setActualSku] = useState('');
  const [expectedQuantity, setExpectedQuantity] = useState('');
  const [actualQuantity, setActualQuantity] = useState('');
  const [pickerId, setPickerId] = useState('');
  const [pickerName, setPickerName] = useState('');
  const [detectedBy, setDetectedBy] = useState('packer');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setErrorType('wrong_item');
    setProductSku('');
    setExpectedSku('');
    setActualSku('');
    setExpectedQuantity('');
    setActualQuantity('');
    setPickerId('');
    setPickerName('');
    setDetectedBy('packer');
    setNotes('');
    setError(null);
  };

  const handleSave = async () => {
    setError(null);

    const payload: Record<string, unknown> = {
      error_type: errorType,
      detected_by: detectedBy,
    };

    if (orderId) payload.order_id = orderId;
    if (productSku) payload.product_sku = productSku;
    if (expectedSku) payload.expected_sku = expectedSku;
    if (actualSku) payload.actual_sku = actualSku;
    if (expectedQuantity) payload.expected_quantity = parseInt(expectedQuantity, 10);
    if (actualQuantity) payload.actual_quantity = parseInt(actualQuantity, 10);
    if (pickerId) payload.picker_id = pickerId;
    if (pickerName) payload.picker_name = pickerName;
    if (notes) payload.notes = notes;

    const result = await fetchApi('/reports/errors', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (result) {
      resetForm();
      onSaved();
      onClose();
    } else {
      setError('Failed to log error');
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Log Picking Error"
      primaryAction={{
        content: 'Log Error',
        onAction: handleSave,
        loading,
      }}
      secondaryActions={[
        { content: 'Cancel', onAction: handleClose },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}

          {orderNumber && (
            <Banner tone="info">
              Logging error for order: <strong>{orderNumber}</strong>
            </Banner>
          )}

          <Select
            label="Error Type"
            options={[
              { label: 'Wrong Item Picked', value: 'wrong_item' },
              { label: 'Wrong Quantity', value: 'wrong_quantity' },
              { label: 'Missed Item', value: 'missed_item' },
              { label: 'Picked from Wrong Location', value: 'wrong_location' },
              { label: 'Damaged During Pick', value: 'damaged' },
            ]}
            requiredIndicator value={errorType}
            onChange={setErrorType}
          />

          <TextField
            label="Product SKU"
            value={productSku}
            onChange={setProductSku}
            autoComplete="off"
            placeholder="SKU of the affected product"
          />

          {(errorType === 'wrong_item' || errorType === 'wrong_quantity') && (
            <InlineStack gap="400" wrap>
              {errorType === 'wrong_item' && (
                <>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <TextField
                      label="Expected SKU"
                      value={expectedSku}
                      onChange={setExpectedSku}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <TextField
                      label="Actual SKU Picked"
                      value={actualSku}
                      onChange={setActualSku}
                      autoComplete="off"
                    />
                  </div>
                </>
              )}
              {errorType === 'wrong_quantity' && (
                <>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <TextField
                      label="Expected Qty"
                      type="number"
                      value={expectedQuantity}
                      onChange={setExpectedQuantity}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <TextField
                      label="Actual Qty Picked"
                      type="number"
                      value={actualQuantity}
                      onChange={setActualQuantity}
                      autoComplete="off"
                    />
                  </div>
                </>
              )}
            </InlineStack>
          )}

          <InlineStack gap="400" wrap>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <TextField
                label="Picker ID (optional)"
                value={pickerId}
                onChange={setPickerId}
                autoComplete="off"
              />
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <TextField
                label="Picker Name (optional)"
                value={pickerName}
                onChange={setPickerName}
                autoComplete="off"
              />
            </div>
          </InlineStack>

          <Select
            label="Detected By"
            options={[
              { label: 'Packer', value: 'packer' },
              { label: 'Customer', value: 'customer' },
              { label: 'QA Check', value: 'qa' },
              { label: 'Driver', value: 'driver' },
            ]}
            value={detectedBy}
            onChange={setDetectedBy}
          />

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={setNotes}
            autoComplete="off"
            multiline={3}
            placeholder="Additional details about the error..."
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
